import { useState, useEffect, useRef } from 'react'
import SearchModal from './SearchModal'
import PostModal from './PostModal'
import NotificationBell from './NotificationBell'
import KeybindsModal from './KeybindsModal'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { ROLE_LABELS, canWrite, isAdmin, canModerate } from '../lib/supabase'
import { getTierByCi } from '../lib/press'

// Inject responsive styles once
const STYLE_ID = 'margins-nav-styles'
if (typeof document !== 'undefined' && !document.getElementById(STYLE_ID)) {
  const s = document.createElement('style')
  s.id = STYLE_ID
  s.textContent = `
    @keyframes dropdownIn { from { opacity:0; transform:translateY(-6px); } to { opacity:1; transform:translateY(0); } }
    @keyframes mobileMenuIn { from { opacity:0; transform:translateY(-8px); } to { opacity:1; transform:translateY(0); } }
    .nav-links-desktop { display: flex !important; }
    .nav-hamburger { display: none !important; }
    .nav-dropdown-item:hover { background: var(--paper) !important; }
    @media (max-width: 920px) {
      .nav-links-desktop { display: none !important; }
      .nav-hamburger { display: flex !important; }
    }
  `
  document.head.appendChild(s)
}

export default function Navbar() {
  const { user, profile, loading: authLoading, signOut } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [menuOpen, setMenuOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [searchPostId, setSearchPostId] = useState(null)
  const [mobileOpen, setMobileOpen] = useState(false)
  const [scrolled, setScrolled] = useState(false)
  const [dark, setDark] = useState(() => localStorage.getItem('margins_theme') !== 'light')
  const [keybindsOpen, setKeybindsOpen] = useState(false)
  const dropdownRef = useRef(null)

  useEffect(() => {
    // Ensure dark is set as default if no preference stored
    if (!localStorage.getItem('margins_theme')) {
      localStorage.setItem('margins_theme', 'dark')
    }
    document.documentElement.setAttribute('data-theme', dark ? '' : 'light')
    localStorage.setItem('margins_theme', dark ? 'dark' : 'light')
  }, [dark])

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => window.removeEventListener('scroll', handleScroll)
  }, [])

  useEffect(() => {
    setMobileOpen(false)
    setMenuOpen(false)
  }, [location.pathname])

  useEffect(() => {
    function handleOutside(e) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setMenuOpen(false)
    }
    document.addEventListener('mousedown', handleOutside)
    return () => document.removeEventListener('mousedown', handleOutside)
  }, [])

  useEffect(() => {
    function handleKey(e) {
      const tag = document.activeElement?.tagName
      const editable = document.activeElement?.contentEditable === 'true'
      // ? opens keybinds
      if (e.key === '?' && tag !== 'INPUT' && tag !== 'TEXTAREA' && !editable) {
        e.preventDefault()
        setKeybindsOpen(k => !k)
      }
      // Ctrl/⌘ + / opens search
      if ((e.ctrlKey || e.metaKey) && e.key === '/') {
        e.preventDefault()
        setSearchOpen(true)
      }
    }
    window.addEventListener('keydown', handleKey)
    return () => window.removeEventListener('keydown', handleKey)
  }, [])

  async function handleSignOut() {
    await signOut()
    navigate('/')
  }

  const isActive = (path, exact = false) =>
    exact ? location.pathname === path : (location.pathname === path || location.pathname.startsWith(path + '/'))

  const navLinks = [
    { to: '/feed', label: 'feed' },
    { to: '/prompts', label: 'prompts' },
    { to: '/community', label: 'community' },
    { to: '/about', label: 'about' },
  ]

  return (
    <>
      {keybindsOpen && <KeybindsModal onClose={() => setKeybindsOpen(false)} />}
      <nav style={{
        position: 'fixed', top: 0, left: 0, right: 0, zIndex: 900,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: scrolled ? '0.8rem 3rem' : '1.1rem 3rem',
        background: 'var(--nav-bg)',
        borderBottom: '1px solid var(--rule)',
        backdropFilter: 'blur(12px)',
        transition: 'padding 0.3s ease, box-shadow 0.3s ease',
        boxShadow: scrolled ? '0 2px 20px rgba(0,0,0,0.08)' : 'none',
      }}>
        <Link to="/" style={styles.logo} aria-label="The Margins — home">
          <svg
            viewBox="0 0 198 52"
            xmlns="http://www.w3.org/2000/svg"
            style={{ display: 'block', height: 36, width: 'auto', overflow: 'visible' }}
          >
            <text
              x="0" y="22"
              fontFamily="'Playfair Display', Georgia, 'Times New Roman', serif"
              fontWeight="700"
              fontSize="22"
              fill="var(--ink)"
              letterSpacing="-0.3"
            >The</text>
            <text
              x="0" y="48"
              fontFamily="'Playfair Display', Georgia, 'Times New Roman', serif"
              fontWeight="700"
              fontSize="22"
              fill="var(--ink)"
              letterSpacing="-0.3"
            >Margins<tspan fill="#C8401A" letterSpacing="0">.</tspan></text>
          </svg>
        </Link>

        {/* Desktop nav */}
        <div className="nav-links-desktop" style={styles.links}>
          {navLinks.map(({ to, label, exact }) => (
            to.includes('#') ? (
              <a key={to} href={to} style={{
                ...styles.link,
                ...(location.pathname === '/' ? styles.activeLink : {})
              }}>{label}</a>
            ) : (
              <Link key={to} to={to} style={{
                ...styles.link,
                ...(isActive(to, exact) ? styles.activeLink : {})
              }}>{label}</Link>
            )
          ))}
          {user && canWrite(profile?.role) && (
            <Link to="/write" style={styles.writeBtn} className="nav-write-btn">
              ✦ write
            </Link>
          )}
          <button onClick={() => setSearchOpen(true)} style={styles.searchBtn} title="Search (Ctrl+K)">⌕</button>

          <button onClick={() => setDark(d => !d)} style={styles.themeBtn} title={dark ? 'Light mode' : 'Dark mode'}>
            {dark
              ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
              : <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
            }
          </button>

          {user ? (
            <div style={{ position: 'relative' }} ref={dropdownRef}>
              <button style={styles.avatarBtn} onClick={() => setMenuOpen(!menuOpen)}>
                <span style={styles.avatar}>{profile?.display_name?.[0]?.toUpperCase() || 'U'}</span>
                <span style={styles.avatarName}>{profile?.display_name}</span>
                <span style={{ fontSize: '0.55rem', color: 'var(--muted)', display: 'inline-block', transition: 'transform 0.2s', transform: menuOpen ? 'rotate(180deg)' : 'none' }}>▾</span>
              </button>
              {menuOpen && (
                <div style={styles.dropdown}>
                  <div style={{ padding: '1rem 1.2rem 0.8rem' }}>
                    <div style={styles.dropdownEmail}>{user.email}</div>
                    {!authLoading && profile && (() => {
                      const tier = getTierByCi(profile.column_inches || 0, profile.is_voice)
                      return (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.4rem' }}>
                          <span style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: '0.9rem', color: tier.color, lineHeight: 1 }}>{tier.badge}</span>
                          <div>
                            <div style={{ fontFamily: "'Courier New', monospace", fontSize: '0.58rem', letterSpacing: '0.12em', color: tier.color, textTransform: 'uppercase' }}>
                              {tier.indicator}
                            </div>
                            <div style={{ fontFamily: "'Courier New', monospace", fontSize: '0.55rem', color: 'var(--muted)', letterSpacing: '0.06em' }}>
                              {tier.title} · {profile.column_inches || 0} CI
                            </div>
                          </div>
                        </div>
                      )
                    })()}
                    {authLoading && <div style={{ ...styles.dropdownRole, color: 'var(--muted)' }}>...</div>}
                  </div>
                  <div style={{ height: 1, background: 'var(--rule)' }} />
                  <div style={{ padding: '0.5rem 0' }}>
                    <NotificationBell inline />
                  </div>
                  <div style={{ height: 1, background: 'var(--rule)' }} />
                  {canWrite(profile?.role) && (
                    <Link to="/write" className="nav-dropdown-item" style={{
                      ...styles.dropdownItem,
                      background: 'var(--accent)', color: '#F0EBE3',
                      fontWeight: 700, letterSpacing: '0.04em',
                      marginBottom: '2px',
                    }}>
                      <span style={{ fontSize: '0.7rem' }}>✦</span> start writing
                    </Link>
                  )}
                  <Link to="/dashboard" className="nav-dropdown-item" style={styles.dropdownItem}>
                    <span style={{ color: 'var(--muted)', fontSize: '0.75rem' }}>◈</span> My Dashboard
                  </Link>
                  <Link to="/submissions" className="nav-dropdown-item" style={styles.dropdownItem}>
                    <span style={{ color: 'var(--muted)', fontSize: '0.75rem' }}>◌</span> My Submissions
                  </Link>
                  {canModerate(profile?.role) && (
                    <Link to="/moderation" className="nav-dropdown-item" style={styles.dropdownItem}>
                      <span style={{ color: 'var(--warning)', fontSize: '0.75rem' }}>◎</span> Moderation
                    </Link>
                  )}
                  {isAdmin(profile?.role) && (
                    <Link to="/admin" className="nav-dropdown-item" style={styles.dropdownItem}>
                      <span style={{ color: 'var(--accent)', fontSize: '0.7rem' }}>⬡</span> Admin Panel
                    </Link>
                  )}
                  <div style={{ height: 1, background: 'var(--rule)' }} />
                  <button
                    style={{ ...styles.dropdownItem, width: '100%', textAlign: 'left', border: 'none', cursor: 'pointer', background: 'none' }}
                    onClick={() => { setMenuOpen(false); setKeybindsOpen(true) }}
                  >
                    <span style={{ color: 'var(--muted)', fontSize: '0.75rem' }}>⌨</span> Shortcuts & Features
                  </button>
                  <div style={{ height: 1, background: 'var(--rule)' }} />
                  <button style={styles.dropdownSignout} onClick={handleSignOut}>Sign Out</button>
                </div>
              )}
            </div>
          ) : (
            <Link to="/auth" style={styles.signInBtn}>Sign In</Link>
          )}
        </div>

        {/* Mobile: theme + hamburger */}
        <div className="nav-hamburger" style={{ display: 'none', alignItems: 'center', gap: '0.75rem' }}>
          <button onClick={() => setDark(d => !d)} style={styles.themeBtn} title={dark ? 'Light mode' : 'Dark mode'}>
            {dark
              ? <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
              : <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
            }
          </button>
          <button
            onClick={() => setMobileOpen(o => !o)}
            style={{ background: 'none', border: '1px solid var(--rule)', padding: '0.45rem', display: 'flex', flexDirection: 'column', gap: '4px', cursor: 'pointer' }}
          >
            <span style={{ display: 'block', width: 18, height: 1.5, background: 'var(--ink)', transition: 'all 0.25s', transform: mobileOpen ? 'translateY(5.5px) rotate(45deg)' : 'none' }} />
            <span style={{ display: 'block', width: 18, height: 1.5, background: 'var(--ink)', opacity: mobileOpen ? 0 : 1, transition: 'opacity 0.25s' }} />
            <span style={{ display: 'block', width: 18, height: 1.5, background: 'var(--ink)', transition: 'all 0.25s', transform: mobileOpen ? 'translateY(-5.5px) rotate(-45deg)' : 'none' }} />
          </button>
        </div>
      </nav>

      {/* Mobile Menu Panel */}
      {mobileOpen && (
        <div style={{
          position: 'fixed', top: 57, left: 0, right: 0, bottom: 0, zIndex: 895,
          background: 'var(--paper)', borderBottom: '1px solid var(--rule)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          animation: 'mobileMenuIn 0.2s ease',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
        }}>
          <div style={{ padding: '1.25rem 1.5rem', display: 'flex', flexDirection: 'column', paddingBottom: '3rem' }}>
            {/* Search */}
            <button
              onClick={() => { setMobileOpen(false); setSearchOpen(true) }}
              style={{ padding: '0.85rem 0', fontFamily: "'DM Mono', monospace", fontSize: '0.72rem', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--muted)', borderBottom: '1px solid var(--rule)', background: 'none', border: 'none', borderBottom: '1px solid var(--rule)', textAlign: 'left', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.6rem' }}
            >
              <span style={{ fontSize: '1rem' }}>⌕</span> Search
            </button>
            {navLinks.map(({ to, label, exact }) => (
              to.includes('#') ? (
                <a key={to} href={to} style={{
                  padding: '0.85rem 0',
                  fontFamily: "'DM Mono', monospace", fontSize: '0.72rem',
                  letterSpacing: '0.12em', textTransform: 'uppercase',
                  color: location.pathname === '/' ? 'var(--ink)' : 'var(--muted)',
                  borderBottom: '1px solid var(--rule)',
                }}>{label}</a>
              ) : (
                <Link key={to} to={to} style={{
                  padding: '0.85rem 0',
                  fontFamily: "'DM Mono', monospace", fontSize: '0.72rem',
                  letterSpacing: '0.12em', textTransform: 'uppercase',
                  color: isActive(to, exact) ? 'var(--ink)' : 'var(--muted)',
                  borderBottom: '1px solid var(--rule)',
                }}>{label}</Link>
              )
            ))}


            <div style={{ height: 1, background: 'var(--rule)', margin: '0.5rem 0' }} />

            {user ? (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '0.75rem 0', borderBottom: '1px solid var(--rule)' }}>
                  <span style={{ ...styles.avatar, width: 30, height: 30, fontSize: '0.8rem' }}>{profile?.display_name?.[0]?.toUpperCase() || 'U'}</span>
                  <div>
                    <div style={{ fontFamily: "'DM Mono', monospace", fontSize: '0.7rem', color: 'var(--ink)' }}>{profile?.display_name}</div>
                    <div style={{ fontFamily: "'DM Mono', monospace", fontSize: '0.6rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: getRoleColor(profile?.role) }}>{authLoading ? '...' : (ROLE_LABELS[profile?.role] || 'Viewer')}</div>
                  </div>
                </div>
                {canWrite(profile?.role) && <Link to="/write" style={{ padding: '0.85rem 0', fontFamily: "'DM Mono', monospace", fontSize: '0.72rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--muted)', borderBottom: '1px solid var(--rule)' }}>✦ Write</Link>}
                <Link to="/dashboard" style={{ padding: '0.85rem 0', fontFamily: "'DM Mono', monospace", fontSize: '0.72rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--muted)', borderBottom: '1px solid var(--rule)' }}>Dashboard</Link>
                <Link to="/submissions" style={{ padding: '0.85rem 0', fontFamily: "'DM Mono', monospace", fontSize: '0.72rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--muted)', borderBottom: '1px solid var(--rule)' }}>My Submissions</Link>
                {canModerate(profile?.role) && <Link to="/moderation" style={{ padding: '0.85rem 0', fontFamily: "'DM Mono', monospace", fontSize: '0.72rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--muted)', borderBottom: '1px solid var(--rule)' }}>Moderation</Link>}
                {isAdmin(profile?.role) && <Link to="/admin" style={{ padding: '0.85rem 0', fontFamily: "'DM Mono', monospace", fontSize: '0.72rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--muted)', borderBottom: '1px solid var(--rule)' }}>Admin Panel</Link>}
                <button onClick={handleSignOut} style={{ background: 'none', border: 'none', textAlign: 'left', padding: '0.85rem 0', fontFamily: "'DM Mono', monospace", fontSize: '0.72rem', letterSpacing: '0.1em', textTransform: 'uppercase', color: 'var(--accent)' }}>Sign Out</button>
              </>
            ) : (
              <Link to="/auth" className="btn btn-ink" style={{ marginTop: '0.5rem', justifyContent: 'center' }}>Sign In</Link>
            )}
          </div>
        </div>
      )}
      {mobileOpen && <div style={{ position: 'fixed', inset: 0, zIndex: 893 }} onClick={() => setMobileOpen(false)} />}
      {searchOpen && (
        <SearchModal
          onClose={() => setSearchOpen(false)}
          onSelectPost={id => { setSearchPostId(id); setSearchOpen(false) }}
        />
      )}
      {searchPostId && (
        <PostModal
          postId={searchPostId}
          onClose={() => setSearchPostId(null)}
        />
      )}
    </>
  )
}

function getRoleColor(role) {
  const colors = {
    viewer: 'var(--muted)',
    writer_blog: '#4a90d9',
    writer_all: '#4a90d9',
    mod: 'var(--warning)',
    admin: 'var(--accent)'
  }
  return colors[role] || 'var(--muted)'
}

const styles = {
  logo: {
    display: 'inline-flex',
    alignItems: 'center',
    lineHeight: 1,
    flexShrink: 0,
  },
  links: { alignItems: 'center', gap: '2.2rem' },
  link: {
    fontFamily: "'DM Mono', monospace",
    fontSize: '0.68rem', letterSpacing: '0.12em',
    textTransform: 'uppercase', color: 'var(--muted)',
    transition: 'color 0.2s',
  },
  activeLink: { color: 'var(--ink)' },
  themeBtn: {
    background: 'none', border: '1px solid var(--rule)',
    color: 'var(--muted)', width: 32, height: 32,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', transition: 'all 0.2s', flexShrink: 0,
  },
  writeBtn: {
    fontFamily: "'DM Mono', monospace",
    fontSize: '0.58rem',
    letterSpacing: '0.12em',
    color: 'var(--paper)',
    background: 'var(--accent)',
    padding: '0.45rem 1rem',
    border: 'none',
    cursor: 'pointer',
    textDecoration: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    transition: 'background .2s',
    whiteSpace: 'nowrap',
  },
  searchBtn: {
    background: 'none', border: '1px solid var(--rule)', width: 34, height: 34,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '1.1rem', color: 'var(--muted)', cursor: 'pointer', transition: 'all 0.15s',
  },
  signInBtn: {
    fontFamily: "'DM Mono', monospace",
    fontSize: '0.68rem', letterSpacing: '0.1em',
    textTransform: 'uppercase', color: 'var(--paper)',
    background: 'var(--ink)', padding: '0.55rem 1.2rem',
    border: '2px solid var(--ink)', transition: 'all 0.2s',
  },
  avatarBtn: {
    display: 'flex', alignItems: 'center', gap: '0.6rem',
    background: 'none', border: '1px solid var(--rule)', padding: '0.4rem 0.8rem',
    color: 'var(--ink)', transition: 'border-color 0.2s',
  },
  avatar: {
    width: 24, height: 24, background: 'var(--accent)',
    color: 'white', display: 'inline-flex', alignItems: 'center',
    justifyContent: 'center', fontSize: '0.7rem', fontWeight: 700,
    borderRadius: '50%', flexShrink: 0,
  },
  avatarName: {
    fontFamily: "'DM Mono', monospace",
    fontSize: '0.65rem', letterSpacing: '0.06em', color: 'var(--ink)',
  },
  dropdown: {
    position: 'absolute', top: 'calc(100% + 0.5rem)', right: 0,
    background: 'var(--cream)', border: '1px solid var(--rule)',
    minWidth: 240, zIndex: 1000,
    boxShadow: '0 8px 40px rgba(0,0,0,0.4)',
    animation: 'dropdownIn 0.15s ease',
    maxHeight: 'calc(100vh - 80px)',
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
  },
  dropdownEmail: { fontFamily: "'DM Mono', monospace", fontSize: '0.63rem', color: 'var(--muted)', marginBottom: '0.3rem' },
  dropdownRole: { fontFamily: "'DM Mono', monospace", fontSize: '0.6rem', letterSpacing: '0.1em', textTransform: 'uppercase', fontWeight: 500 },
  dropdownItem: {
    display: 'flex', alignItems: 'center', gap: '0.6rem',
    padding: '0.75rem 1.2rem',
    fontFamily: "'DM Mono', monospace", fontSize: '0.68rem',
    letterSpacing: '0.06em', color: 'var(--ink)',
    transition: 'background 0.15s', background: 'transparent',
  },
  dropdownSignout: {
    display: 'block', width: '100%', textAlign: 'left',
    padding: '0.75rem 1.2rem', background: 'none', border: 'none',
    fontFamily: "'DM Mono', monospace", fontSize: '0.68rem',
    letterSpacing: '0.06em', color: 'var(--accent)',
    transition: 'background 0.15s',
  },
}
